import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The `error_code` values only the pre-session screens meet. Codes more than
 * one feature meets — the session-lifecycle ones every surface can hit — stay
 * in `@shared/errors/catalog.ts`.
 *
 * **Never inline a backend code in JSX.** The code is a log key; what reaches
 * the operator is the sentence; the `X-Request-ID` stays in the log line.
 *
 * Tone is load-bearing. `AUTH_SMS_DISPATCH_FAILED` and `AUTH_UPSTREAM_TIMEOUT`
 * are upstream failures nobody on this screen caused, so both are neutral: a
 * danger-styled banner for a provider outage teaches an operator that they
 * broke the sign-in. `AUTH_USER_BLOCKED` is danger, because the account really
 * is closed and the sentence has to carry that weight.
 *
 * `AUTH_ORIGIN_NOT_PERMITTED` is the odd one. It means the sign-in reached a
 * host `SESSION_COOKIE_HOSTS` does not name, which is structurally unreachable
 * from this bundle — so it is treated as a contract-drift signal rather than as
 * something the operator can fix, and its copy points at the reference instead
 * of at an action.
 */
export const authErrors: Readonly<Record<string, ErrorCopy>> = {
  // ---- the code request -------------------------------------------------
  AUTH_PHONE_INVALID: {
    message: 'رقم الموبايل غير مقروء. أدخله كاملاً بصيغة يمنية ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  // NOT «the previous code is still valid». `backend-auth` REPLACES a live code
  // on every request — the newest digits win and the old ones die — so nothing
  // is waiting to expire. This 429 is the per-number SPEND cap
  // (`OTP_REQUEST_PER_IDENTIFIER_LIMIT` over `LOGIN_LOCKOUT_WINDOW_SECONDS`),
  // and telling the operator to wait for a code that no longer exists sent them
  // to watch a countdown that could not end their wait.
  AUTH_OTP_RESEND_BARRED: {
    message: 'طلبات كثيرة على هذا الرقم خلال وقت قصير. انتظر قليلاً ثمّ اطلب رمزاً جديداً.',
    tone: 'warning',
  },
  AUTH_RATE_LIMITED: {
    message: 'طلبات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  AUTH_SMS_DISPATCH_FAILED: {
    message: 'تعذّر إرسال الرسالة النصّية الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  AUTH_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت الخدمة عن الردّ. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },

  // ---- the sign-in ------------------------------------------------------
  AUTH_OTP_INVALID: {
    message: 'الرمز غير مطابق. تحقّق من الرسالة، أو اطلب رمزاً جديداً بعد انتهاء المهلة.',
    tone: 'warning',
  },
  AUTH_OTP_EXPIRED: {
    message: 'انتهت صلاحية الرمز. اطلب رمزاً جديداً ثمّ أعد الإدخال.',
    tone: 'warning',
  },
  AUTH_PASSWORD_INVALID: {
    message: 'كلمة المرور غير صحيحة. جرّب الدخول بالرمز، أو أعد تعيين الكلمة.',
    tone: 'warning',
  },
  /**
   * The account exists and has no password — the state every account starts in.
   * The copy names the way in rather than the missing thing, because the
   * operator did nothing wrong by leaving the field empty.
   */
  AUTH_PASSWORD_NOT_SET: {
    message: 'هذا الحساب يدخل بالرمز فقط. اترك حقل كلمة المرور فارغاً واطلب رمز الدخول.',
    tone: 'warning',
  },
  AUTH_METHOD_NOT_PERMITTED: {
    message: 'طريقة الدخول هذه غير مفعّلة. استخدم الطريقة المعروضة على الشاشة.',
    tone: 'warning',
  },
  AUTH_USER_BLOCKED: {
    message: 'هذا الحساب محظور. راجع مسؤولاً لرفع الحظر.',
    tone: 'danger',
  },
  AUTH_LOGIN_LOCKED: {
    message: 'تعذّر الدخول مرّات متتالية، فأُغلق الحساب مؤقّتاً. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'danger',
  },
  AUTH_ORIGIN_NOT_PERMITTED: {
    message: 'تعذّر إتمام الدخول من هذا العنوان. أعد تحميل الصفحة، وإن تكرّر فأبلغ الدعم.',
    tone: 'danger',
  },

  // ---- the recovery -----------------------------------------------------
  AUTH_PASSWORD_MISMATCH: {
    message: 'الكلمتان غير متطابقتين. أعد كتابة التأكيد.',
    tone: 'warning',
  },
};

/**
 * `details[].code` → the field the error belongs under.
 *
 * A field error is mapped from the code, **never from the message text**: the
 * message is prose that changes with a copy edit, and a form that keys on it
 * silently stops attaching errors the day someone rewrites a sentence.
 */
export const AUTH_FIELD_FOR_CODE: Readonly<Record<string, string>> = {
  AUTH_PHONE_INVALID: 'phone',
  AUTH_OTP_INVALID: 'code',
  AUTH_OTP_EXPIRED: 'code',
  AUTH_PASSWORD_INVALID: 'password',
  AUTH_PASSWORD_MISMATCH: 'passwordConfirm',
};
